import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { FanslyError, type PostOptions } from '../api'

/**
 * Runs a text write (message, post, mass message) and, when the CRM flags
 * potentially prohibited wording (422), asks the operator before sending the
 * same thing again with the confirmation. Resolves undefined when the
 * operator cancels.
 */
export function useProhibitedConfirm() {
  const { t } = useTranslation('fansly')
  const [ask, setAsk] = useState<{ terms: string[]; resolve: (yes: boolean) => void } | null>(null)
  const key = useRef(0)

  const run = useCallback(async <T,>(write: (opts: PostOptions) => Promise<T>): Promise<T | undefined> => {
    // One idempotency key for the whole action: the confirmed resend is the same action.
    const idempotencyKey = `act-${Date.now().toString(36)}-${(key.current += 1)}`
    try {
      return await write({ idempotencyKey })
    } catch (e) {
      if (!(e instanceof FanslyError) || !e.needsProhibitedConfirmation) throw e
      const yes = await new Promise<boolean>((resolve) => setAsk({ terms: e.terms, resolve }))
      setAsk(null)
      if (!yes) return undefined
      return write({ idempotencyKey, confirmProhibited: true })
    }
  }, [])

  // Above any open dialog (the composers that use it are often dialogs).
  const node: ReactNode = ask ? (
    <div className="fs-confirm-top">
      <ConfirmPopover
      title={t('prohibited.title')}
      body={t('prohibited.body', { terms: ask.terms.join(', ') })}
      confirmLabel={t('prohibited.send')}
      onConfirm={() => ask.resolve(true)}
        onCancel={() => ask.resolve(false)}
      />
    </div>
  ) : null

  return { run, node }
}
